import React from 'react';

import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { gql } from '@soundxyz/gql-string';
import { SquareImage } from '@components/main-components/image/BaseImage';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { ReleasePlayButtonFragmentDoc } from '@graphql/reactQuery';
import { usePlayButton } from '@hooks/usePlayButton';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleasePlayButton on Release {
    id
    type
    title
    listeningParty
    track {
      id
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    eggGame {
      id
      goldenEggImage {
        id
        url
      }
      animatedGoldenEggImageOptimized {
        id
        url
      }
    }
  }
`);

export const ReleasePlayButton = ({
  playButtonSize,
  releaseFrag,
  goldenEgg,
}: {
  playButtonSize: 'xs' | 's' | 'm';
  releaseFrag: FragmentType<ReleasePlayButtonFragmentDoc>;
  goldenEgg?: boolean;
}) => {
  const {
    id: releaseId,
    coverImage,
    staticCoverImage,
    eggGame,
    track: { id: trackId },
    title,
    type,
    listeningParty,
  } = getFragment(ReleasePlayButtonFragmentDoc, releaseFrag);
  const isAlbum = type === 'ALBUM';
  const songImage =
    (goldenEgg && !!eggGame
      ? eggGame?.animatedGoldenEggImageOptimized?.url || eggGame?.goldenEggImage?.url
      : staticCoverImage?.url || coverImage.url) ?? '';

  const { isPlaying, isLoading, onPlayClick, currentListeningParty } = usePlayButton({
    trackId,
    isAlbum,
    listeningParty,
    releaseId,
  });

  return (
    <div
      className={clsx(
        'group relative flex-shrink-0 overflow-hidden',
        playButtonSize === 'xs' ? 'rounded-sm' : 'rounded-md',
        {
          'h-12 w-12': playButtonSize === 'm',
          'h-10 w-10': playButtonSize === 's',
          'h-8 w-8': playButtonSize === 'xs',
        },
      )}
    >
      <SquareImage
        size={playButtonSize === 'm' ? 48 : playButtonSize === 's' ? 40 : 32}
        borderRadius={playButtonSize === 'xs' ? 4 : 8}
        src={songImage}
        alt={title}
      />
      <PlayButton
        className={clsx(
          'absolute left-0 top-0 group-hover:visible',
          isPlaying ? 'visible' : 'invisible',
        )}
        onClick={onPlayClick}
        size={playButtonSize}
        playing={isPlaying}
        loading={isLoading}
        color="transparent"
        whileTap={undefined}
        whileHover={undefined}
        currentListeningParty={currentListeningParty}
        iconClassname={twMerge(currentListeningParty && 'opacity-50')}
      />
    </div>
  );
};
